import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Mail, Building2, Phone, MessageSquare, Calendar, RefreshCw, Trash2, Lock, LogOut, Eye, EyeOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const api = axios.create({ baseURL: '/api', withCredentials: true });

const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/admin/login', { password });
      if (response.data.success) {
        onLogin();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#105EFF]/10 rounded-full flex items-center justify-center">
            <Lock className="h-8 w-8 text-[#105EFF]" />
          </div>
          <CardTitle className="text-2xl text-white">Admin Access</CardTitle>
          <CardDescription className="text-slate-400">Enter your password to continue</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Input
                data-testid="admin-password-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 pr-10"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="login-error-message">{error}</p>}
            <Button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading || !password}
              className="w-full bg-[#105EFF] hover:bg-[#0D4FCC]"
            >
              {loading ? 'Verifying...' : 'Unlock Dashboard'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-slate-400 hover:text-[#105EFF] text-sm">← Back to Home</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ConsultationCard = ({ consultation, deletingId, onDelete, formatDate }) => (
  <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`consultation-card-${consultation.id}`}>
    <CardHeader>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
            {consultation.name}
            <Badge className="bg-[#105EFF] hover:bg-[#0D4FCC]">{consultation.status}</Badge>
          </CardTitle>
          <CardDescription className="flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4" /> {formatDate(consultation.created_at)}
          </CardDescription>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => onDelete(consultation.id)}
          disabled={deletingId === consultation.id}
          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid={`delete-button-${consultation.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex items-start gap-2">
          <Mail className="h-4 w-4 text-[#105EFF] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-slate-600">Email</p>
            <a href={`mailto:${consultation.email}`} className="text-slate-900 hover:text-[#105EFF]">{consultation.email}</a>
          </div>
        </div>
        {consultation.company && (
          <div className="flex items-start gap-2">
            <Building2 className="h-4 w-4 text-[#105EFF] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-slate-600">Company</p>
              <p className="text-slate-900">{consultation.company}</p>
            </div>
          </div>
        )}
        {consultation.phone && (
          <div className="flex items-start gap-2">
            <Phone className="h-4 w-4 text-[#105EFF] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-slate-600">Phone</p>
              <a href={`tel:${consultation.phone}`} className="text-slate-900 hover:text-[#105EFF]">{consultation.phone}</a>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-start gap-2 bg-slate-50 p-4 rounded-lg">
        <MessageSquare className="h-4 w-4 text-[#105EFF] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-slate-600 mb-1">Message</p>
          <p className="text-slate-900">{consultation.message}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = ({ onLogout }) => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchConsultations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/consultations');
      if (response.data.success) {
        setConsultations(response.data.consultations);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  const handleDelete = async (consultationId) => {
    if (!window.confirm('Are you sure you want to delete this consultation request?')) return;
    setDeletingId(consultationId);
    try {
      const response = await api.delete(`/consultations/${consultationId}`);
      if (response.data.success) {
        toast({ title: "Deleted Successfully", description: "Consultation request has been removed." });
        setConsultations(prev => prev.filter(c => c.id !== consultationId));
      }
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast({ title: "Delete Failed", description: err.response?.data?.detail || "Failed to delete.", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => { fetchConsultations(); }, [fetchConsultations]);

  const filteredConsultations = consultations.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.company && c.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
    c.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                <Shield className="h-8 w-8 text-[#105EFF]" />
                Admin Panel
              </h1>
              <p className="text-slate-600 mt-1">Consultation Request Management</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchConsultations} className="bg-[#105EFF] hover:bg-[#0D4FCC]" data-testid="refresh-button">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button onClick={onLogout} variant="outline" className="border-slate-300" data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
          <div className="flex gap-4">
            <Input
              data-testid="search-input"
              placeholder="Search by name, email, company, or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Requests</CardDescription>
              <CardTitle className="text-3xl text-[#105EFF]" data-testid="total-requests">{consultations.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>New Requests</CardDescription>
              <CardTitle className="text-3xl text-green-600" data-testid="new-requests">
                {consultations.filter(c => c.status === 'new').length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Filtered Results</CardDescription>
              <CardTitle className="text-3xl text-slate-600" data-testid="filtered-results">{filteredConsultations.length}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {error && (
          <Card className="border-red-200 bg-red-50 mb-6">
            <CardContent className="pt-6"><p className="text-red-800">{error}</p></CardContent>
          </Card>
        )}

        {loading ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-[#105EFF]" />
                <span className="ml-3 text-slate-600">Loading consultations...</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredConsultations.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-slate-600 py-12" data-testid="no-results-message">No consultation requests found.</p>
                </CardContent>
              </Card>
            ) : (
              filteredConsultations.map((consultation) => (
                <ConsultationCard
                  key={consultation.id}
                  consultation={consultation}
                  deletingId={deletingId}
                  onDelete={handleDelete}
                  formatDate={formatDate}
                />
              ))
            )}
          </div>
        )}

        <div className="mt-8 text-center">
          <a href="/" className="text-[#105EFF] hover:underline">← Back to Home</a>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const [authenticated, setAuthenticated] = useState(null); // null = checking

  const checkAuth = useCallback(() => {
    api.get('/admin/verify')
      .then(() => setAuthenticated(true))
      .catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = useCallback(() => {
    api.post('/admin/logout').finally(() => setAuthenticated(false));
  }, []);

  if (authenticated === null) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-[#105EFF]" />
      </div>
    );
  }

  if (!authenticated) return <AdminLogin onLogin={() => setAuthenticated(true)} />;
  return <AdminDashboard onLogout={handleLogout} />;
};

export default Admin;
